import { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useLang } from "@/contexts/LanguageContext";
import { resolveImageUrl } from "@/lib/api";
import {
  ShoppingBag,
  LogOut,
  ShieldCheck,
  Stethoscope,
  Languages,
  Menu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { settings } = useSettings();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname.startsWith("/dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);

  const brandRaw = settings?.site_name || "PeptideSeries";
  const brandWords = brandRaw.trim().split(/\s+/);
  let brandPrefix;
  let brandSuffix;
  if (brandWords.length > 1) {
    brandPrefix = brandWords.slice(0, -1).join(" ");
    brandSuffix = brandWords[brandWords.length - 1];
  } else if (brandRaw.toLowerCase().startsWith("peptide")) {
    brandPrefix = "Peptide";
    brandSuffix = brandRaw.replace(/^Peptide/i, "") || brandRaw;
  } else {
    brandPrefix = brandRaw;
    brandSuffix = brandRaw;
  }

  const navLink = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${
      isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
    }`;

  const mobileNavLink = ({ isActive }) =>
    `block py-3 px-4 text-base border-b border-border ${
      isActive ? "text-foreground font-medium" : "text-muted-foreground"
    }`;

  const NAV = [
    { to: "/catalog", key: "nav.catalog", testid: "nav-catalog" },
    { to: "/catalog?category=anti-aging", key: "nav.antiaging", testid: "nav-antiaging" },
    { to: "/catalog?category=weight-loss", key: "nav.weightloss", testid: "nav-weightloss" },
    { to: "/catalog?category=fitness", key: "nav.fitness", testid: "nav-fitness" },
    { to: "/about", key: "nav.quality", testid: "nav-about" },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/75 border-b border-border"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-10 h-16 gap-2">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group shrink-0">
          {settings?.logo_url ? (
            <img
              src={resolveImageUrl(settings.logo_url)}
              alt={brandRaw}
              className="h-8 w-auto max-w-[120px] object-contain"
            />
          ) : (
            <span className="w-7 h-7 rounded-sm bg-foreground grid place-items-center">
              <span className="block w-2.5 h-2.5 rounded-full bg-[hsl(var(--accent))]" />
            </span>
          )}
          <span className="font-display text-base sm:text-lg font-semibold tracking-tight">
            {brandPrefix}
            <span className="text-[hsl(var(--accent))]">{brandSuffix}</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={navLink} data-testid={n.testid}>
              {t(n.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2">
          {isDashboard && (
            <button
              type="button"
              onClick={() => setLang(lang === "en" ? "es" : "en")}
              data-testid="lang-toggle"
              className="inline-flex items-center gap-1 h-8 px-2 sm:px-2.5 text-[11px] sm:text-xs font-mono uppercase tracking-widest border border-border rounded-md hover:bg-secondary"
              aria-label="Toggle language"
            >
              <Languages className="w-3.5 h-3.5" />
              {lang === "en" ? "EN" : "ES"}
            </button>
          )}

          <Link to="/cart" data-testid="cart-button">
            <Button variant="ghost" size="sm" className="relative px-2 sm:px-3">
              <ShoppingBag className="w-4 h-4" />
              <span className="ml-2 hidden md:inline text-sm">{t("nav.cart")}</span>
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-1 -right-1 w-5 h-5 grid place-items-center text-[10px] font-mono rounded-full bg-[hsl(var(--accent))] text-white"
                >
                  {count}
                </span>
              )}
            </Button>
          </Link>

          {!user || user === false ? (
            <>
              <Link to="/login" data-testid="login-link" className="hidden sm:block">
                <Button variant="ghost" size="sm">{t("nav.signin")}</Button>
              </Link>
              <Link to="/register" data-testid="register-link" className="hidden sm:block">
                <Button size="sm" className="rounded-md">{t("nav.register")}</Button>
              </Link>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="user-menu-trigger" className="gap-2 px-2 sm:px-3">
                  {user.role === "admin" && <ShieldCheck className="w-4 h-4" />}
                  {user.role === "doctor" && <Stethoscope className="w-4 h-4" />}
                  <span className="hidden sm:inline">{user.name?.split(" ")[0] || t("nav.account")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest">
                  {user.role}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  {t("nav.dashboard")}
                </DropdownMenuItem>
                {user.role === "doctor" && (
                  <DropdownMenuItem onClick={() => navigate("/dashboard/verification")} data-testid="menu-verification">
                    {t("nav.verification")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/cart")}>{t("nav.cart")}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  data-testid="menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" /> {t("nav.signout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Mobile hamburger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden px-2"
                data-testid="mobile-menu-trigger"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] sm:w-[380px] p-0 flex flex-col">
              <SheetHeader className="px-4 py-3 border-b border-border">
                <SheetTitle className="font-display text-base text-left">
                  {brandPrefix}<span className="text-[hsl(var(--accent))]">{brandSuffix}</span>
                </SheetTitle>
              </SheetHeader>

              <nav className="flex-1 overflow-auto" onClick={() => setMobileOpen(false)}>
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    className={mobileNavLink}
                    data-testid={`mobile-${n.testid}`}
                  >
                    {t(n.key)}
                  </NavLink>
                ))}

                {(!user || user === false) && (
                  <>
                    <NavLink to="/login" className={mobileNavLink} data-testid="mobile-login-link">
                      {t("nav.signin")}
                    </NavLink>
                    <NavLink to="/register" className={mobileNavLink} data-testid="mobile-register-link">
                      {t("nav.register")}
                    </NavLink>
                  </>
                )}

                {user && user !== false && (
                  <>
                    <NavLink to="/dashboard" className={mobileNavLink} data-testid="mobile-dashboard">
                      {t("nav.dashboard")}
                    </NavLink>
                    {user.role === "doctor" && (
                      <NavLink to="/dashboard/verification" className={mobileNavLink} data-testid="mobile-verification">
                        {t("nav.verification")}
                      </NavLink>
                    )}
                  </>
                )}
                <NavLink to="/cart" className={mobileNavLink} data-testid="mobile-cart">
                  {t("nav.cart")} {count > 0 && <span className="ml-2 font-mono text-xs">({count})</span>}
                </NavLink>
              </nav>

              {isDashboard && (
                <div className="border-t border-border p-4 flex items-center justify-between">
                  <span className="overline">Language</span>
                  <button
                    type="button"
                    onClick={() => setLang(lang === "en" ? "es" : "en")}
                    data-testid="mobile-lang-toggle"
                    className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-mono uppercase tracking-widest border border-border rounded-md hover:bg-secondary"
                  >
                    <Languages className="w-3.5 h-3.5" />
                    {lang === "en" ? "EN → ES" : "ES → EN"}
                  </button>
                </div>
              )}

              {user && user !== false && (
                <button
                  onClick={async () => {
                    setMobileOpen(false);
                    await logout();
                    navigate("/");
                  }}
                  data-testid="mobile-logout"
                  className="border-t border-border p-4 text-left text-sm text-muted-foreground hover:bg-secondary flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" /> {t("nav.signout")}
                </button>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
